import type { components } from '../api/active/generated/openapi';
import { ActiveApiError, type ActiveTransport } from '../api/active/transport';
import type {
  OperationLog,
  OperationLogListQuery,
  OperationLogPage,
  OperationLogPort,
} from './operationLogPort';

type OperationLogDto = components['schemas']['OperationLogItem'];
type OperationLogDetailDto = components['schemas']['OperationLogDetail'];
type OperationLogPageDto = components['schemas']['OperationLogPage'];

function mapOperationLog(dto: OperationLogDto): OperationLog {
  return {
    id: dto.id,
    actorUserId: dto.actorUserId,
    actorName: dto.actorName,
    occurredAt: dto.occurredAt,
    resourceType: dto.resourceType,
    resourceId: dto.resourceId,
    resourceName: dto.resourceName,
    action: dto.action,
    source: dto.source,
    hasDetails: dto.hasDetails,
    ...(dto.quoteTarget ? { quoteTarget: { ...dto.quoteTarget } } : {}),
    ...(dto.cause ? { cause: { ...dto.cause } } : {}),
  };
}

export function createOperationLogApi(
  transport: ActiveTransport,
): OperationLogPort {
  // Use the session-aware dispatch shared with quotes. `request` on the base
  // transport closes over the original dispatch and bypasses session recovery.
  async function read<T>(path: string, signal?: AbortSignal): Promise<T> {
    const response = await transport.requestResponse(path, {
      signal,
      cache: 'no-store',
    });
    if (!response.ok) {
      const problem = await response.json().catch(() => ({}));
      throw new ActiveApiError(
        response.status,
        response.status === 401
          ? '当前没有有效会话或会话已失效。'
          : `后端请求失败（HTTP ${response.status}）。`,
        typeof problem?.code === 'string' ? problem.code : undefined,
        typeof problem?.title === 'string' ? problem.title : undefined,
      );
    }
    return (await response.json()) as T;
  }
  return {
    async list(query: OperationLogListQuery, signal?: AbortSignal) {
      const params = new URLSearchParams({
        page: String(query.page),
        pageSize: String(query.pageSize),
      });
      if (query.resourceType) params.set('resourceType', query.resourceType);
      if (query.actorUserId?.trim())
        params.set('actorUserId', query.actorUserId.trim());
      if (query.search?.trim()) params.set('search', query.search.trim());
      if (query.from) params.set('from', query.from);
      if (query.to) params.set('to', query.to);
      const dto = await read<OperationLogPageDto>(
        `/operation-logs?${params.toString()}`,
        signal,
      );
      return {
        items: dto.items.map(mapOperationLog),
        total: dto.total,
        page: dto.page,
        pageSize: dto.pageSize,
      } satisfies OperationLogPage;
    },
    async detail(logId: string, signal?: AbortSignal) {
      const dto = await read<OperationLogDetailDto>(
        `/operation-logs/${encodeURIComponent(logId)}`,
        signal,
      );
      return {
        ...mapOperationLog(dto),
        changes: dto.changes.map((change) => ({ ...change })),
        historyStatus: dto.historyStatus,
        ...(dto.references
          ? {
              references: dto.references.map((reference) => ({ ...reference })),
            }
          : {}),
      };
    },
  };
}
